import React from "react";
import { Layout, Menu, Result, Modal } from "antd";
import { Routes, Route, Navigate } from "react-router-dom";
import PageLoader from "./PageLoader";
import MigrationList from "./MigrationList";
import MigrationDetails from "./MigrationDetails";
import MigrationRunList from "./MigrationRunList";
import MigrationRunDetails from "./MigrationRunDetails";
import AgentList from "./AgentList";
import AgentDetails from "./AgentDetails";
import UserList from "./UserList";
import UserEdit from "./UserEdit";
import UserCreate from "./UserCreate";
import { appName } from "../lib/env";
import { signOut } from "../lib/auth-client";
import { usePermissions } from "../lib/usePermissions";

const { Header, Content, Footer } = Layout;

function AuthenticatedLayout({
  user,
  isDarkMode,
}: {
  user: any;
  isDarkMode: boolean;
}) {
  const {
    hasPermission,
    permissionGate,
    defaultPath,
    loading: permLoading,
  } = usePermissions();

  if (permLoading) {
    return (
      <PageLoader
        style={{ display: "flex", justifyContent: "center", marginTop: 120 }}
      />
    );
  }

  let menuItems = [];
  if (hasPermission("migrations.manage")) {
    menuItems.push({
      key: "migrations",
      label: <a href="/migrations">Migrations</a>,
    });
  }
  if (hasPermission("migrations_runs.manage")) {
    menuItems.push({
      key: "migration-runs",
      label: <a href="/migration-runs">Migration Runs</a>,
    });
  }
  if (hasPermission("agents.manage")) {
    menuItems.push({ key: "agents", label: <a href="/agents">Agents</a> });
  }
  if (hasPermission("users.manage")) {
    menuItems.push({ key: "users", label: <a href="/users">Users</a> });
  }
  menuItems.push({
    key: "signout",
    label: (
      <a
        onClick={(e) => {
          e.preventDefault();
          Modal.confirm({
            title: "Sign out",
            content: "Are you sure you want to sign out?",
            okText: "Sign out",
            cancelText: "Cancel",
            onOk: () => signOut(),
          });
        }}
        href="#"
      >
        Sign out ({user?.name})
      </a>
    ),
    style: { marginLeft: "auto" },
  });

  return (
    <Layout className="layout">
      <Header>
        <Menu
          key="main"
          mode="horizontal"
          style={{ lineHeight: "64px" }}
          items={menuItems}
          theme={isDarkMode ? "dark" : "light"}
        />
      </Header>
      <Content style={{ padding: "40px 40px" }}>
        <div className="main-content">
          <Routes>
            <Route path="/" element={<Navigate to={defaultPath} replace />} />
            <Route
              path="/migrations"
              element={permissionGate("migrations.manage", <MigrationList />)}
            />
            <Route
              path="/migrations/:migration_id"
              element={permissionGate(
                "migrations.manage",
                <MigrationDetails />,
              )}
            />
            <Route
              path="/migration-runs"
              element={permissionGate(
                "migrations_runs.manage",
                <MigrationRunList />,
              )}
            />
            <Route
              path="/migration-runs/:migration_run_id"
              element={permissionGate(
                "migrations_runs.manage",
                <MigrationRunDetails />,
              )}
            />
            <Route
              path="/agents"
              element={permissionGate("agents.manage", <AgentList />)}
            />
            <Route
              path="/agents/:agent_id"
              element={permissionGate("agents.manage", <AgentDetails />)}
            />
            <Route
              path="/users"
              element={permissionGate("users.manage", <UserList />)}
            />
            <Route
              path="/users/new"
              element={permissionGate("users.manage", <UserCreate />)}
            />
            <Route
              path="/users/:user_id"
              element={permissionGate("users.manage", <UserEdit />)}
            />
            <Route
              path="/no-access"
              element={
                <Result
                  status="403"
                  title="No Access"
                  subTitle="You don't have permission to view any section. Please contact an administrator."
                />
              }
            />
            <Route
              path="/404"
              element={
                <Result
                  status="404"
                  title="Not Found"
                  subTitle="Sorry, the page you visited does not exist."
                />
              }
            />
            <Route path="*" element={<Navigate to="/404" replace />} />
          </Routes>
        </div>
      </Content>
      <Footer style={{ textAlign: "center" }}>{appName}</Footer>
    </Layout>
  );
}

export default AuthenticatedLayout;
